import React, { useMemo, useState } from "react";

type PaymentStatus = "Paid" | "Pending" | "Failed" | "Refunded";
type PaymentMode = "UPI" | "Card" | "Net Banking" | "Cash" | "Wallet";
type PageMode = "list" | "form";

type Payment = {
  id: number;
  invoiceId: string;
  studentName: string;
  phone: string;
  email: string;
  course: string;
  batch: string;
  amount: number;
  discount: number;
  paidAmount: number;
  dueAmount: number;
  mode: PaymentMode;
  transactionId: string;
  paymentDate: string;
  dueDate: string;
  status: PaymentStatus;
  receiptSent: boolean;
  notes: string;
};

type PaymentForm = Omit<
  Payment,
  "id" | "invoiceId" | "paidAmount" | "dueAmount" | "receiptSent"
>;

const initialPayments: Payment[] = [
  {
    id: 1,
    invoiceId: "MED-INV-1001",
    studentName: "Priya Sharma",
    phone: "9876543210",
    email: "priya.sharma@gmail.com",
    course: "NEET Complete Preparation",
    batch: "NEET 2026 Morning Batch",
    amount: 4999,
    discount: 500,
    paidAmount: 4499,
    dueAmount: 0,
    mode: "UPI",
    transactionId: "UPI987654321",
    paymentDate: "2026-06-15",
    dueDate: "2026-06-15",
    status: "Paid",
    receiptSent: true,
    notes: "Full payment received for NEET batch.",
  },
  {
    id: 2,
    invoiceId: "MED-INV-1002",
    studentName: "Aman Verma",
    phone: "9123456780",
    email: "aman.verma@gmail.com",
    course: "JEE Main + Advanced",
    batch: "JEE Main Target Batch",
    amount: 5499,
    discount: 0,
    paidAmount: 3000,
    dueAmount: 2499,
    mode: "Card",
    transactionId: "CARD124589",
    paymentDate: "2026-06-14",
    dueDate: "2026-06-20",
    status: "Pending",
    receiptSent: false,
    notes: "Partial payment received. Remaining amount pending.",
  },
  {
    id: 3,
    invoiceId: "MED-INV-1003",
    studentName: "Riya Singh",
    phone: "9988776655",
    email: "riya.singh@gmail.com",
    course: "Class 8 Science Foundation",
    batch: "Class 8 Science Foundation",
    amount: 2999,
    discount: 300,
    paidAmount: 0,
    dueAmount: 2699,
    mode: "Net Banking",
    transactionId: "NB789456123",
    paymentDate: "2026-06-13",
    dueDate: "2026-06-18",
    status: "Failed",
    receiptSent: false,
    notes: "Payment failed at gateway. Need follow-up.",
  },
  {
    id: 4,
    invoiceId: "MED-INV-1004",
    studentName: "Mohit Kumar",
    phone: "9012345678",
    email: "mohit.kumar@gmail.com",
    course: "Class 7 Maths Booster",
    batch: "Class 7 Maths Booster",
    amount: 2499,
    discount: 0,
    paidAmount: 2499,
    dueAmount: 0,
    mode: "Cash",
    transactionId: "CASH-001",
    paymentDate: "2026-06-12",
    dueDate: "2026-06-12",
    status: "Paid",
    receiptSent: true,
    notes: "Cash collected at center.",
  },
  {
    id: 5,
    invoiceId: "MED-INV-1005",
    studentName: "Sneha Gupta",
    phone: "9345678120",
    email: "sneha.gupta@gmail.com",
    course: "NEET Complete Preparation",
    batch: "NEET 2026 Morning Batch",
    amount: 4999,
    discount: 0,
    paidAmount: 4999,
    dueAmount: 0,
    mode: "Wallet",
    transactionId: "WLT456789",
    paymentDate: "2026-06-11",
    dueDate: "2026-06-11",
    status: "Refunded",
    receiptSent: true,
    notes: "Refund processed due to course cancellation request.",
  },
];

const emptyForm: PaymentForm = {
  studentName: "",
  phone: "",
  email: "",
  course: "NEET Complete Preparation",
  batch: "NEET 2026 Morning Batch",
  amount: 4999,
  discount: 0,
  mode: "UPI",
  transactionId: "",
  paymentDate: "",
  dueDate: "",
  status: "Paid",
  notes: "",
};

const statuses = ["All", "Paid", "Pending", "Failed", "Refunded"];
const modes = ["All", "UPI", "Card", "Net Banking", "Cash", "Wallet"];

const AdminPayments: React.FC = () => {
  const [payments, setPayments] = useState<Payment[]>(initialPayments);
  const [pageMode, setPageMode] = useState<PageMode>("list");
  const [editingId, setEditingId] = useState<number | null>(null);

  const [search, setSearch] = useState("");
  const [activeStatus, setActiveStatus] = useState("All");
  const [activeMode, setActiveMode] = useState("All");
  const [form, setForm] = useState<PaymentForm>(emptyForm);

  const filteredPayments = useMemo(() => {
    return payments.filter((payment) => {
      const matchStatus =
        activeStatus === "All" || payment.status === activeStatus;
      const matchMode = activeMode === "All" || payment.mode === activeMode;

      const searchText = search.toLowerCase();

      const matchSearch =
        payment.studentName.toLowerCase().includes(searchText) ||
        payment.phone.includes(search) ||
        payment.email.toLowerCase().includes(searchText) ||
        payment.course.toLowerCase().includes(searchText) ||
        payment.invoiceId.toLowerCase().includes(searchText) ||
        payment.transactionId.toLowerCase().includes(searchText);

      return matchStatus && matchMode && matchSearch;
    });
  }, [payments, search, activeStatus, activeMode]);

  const totalRevenue = payments
    .filter((payment) => payment.status === "Paid")
    .reduce((sum, payment) => sum + payment.paidAmount, 0);

  const pendingAmount = payments
    .filter(
      (payment) => payment.status === "Pending" || payment.status === "Failed"
    )
    .reduce((sum, payment) => sum + payment.dueAmount, 0);

  const refundedAmount = payments
    .filter((payment) => payment.status === "Refunded")
    .reduce((sum, payment) => sum + payment.paidAmount, 0);

  const openCreatePage = () => {
    setEditingId(null);
    setForm(emptyForm);
    setPageMode("form");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openEditPage = (payment: Payment) => {
    setEditingId(payment.id);
    setForm({
      studentName: payment.studentName,
      phone: payment.phone,
      email: payment.email,
      course: payment.course,
      batch: payment.batch,
      amount: payment.amount,
      discount: payment.discount,
      mode: payment.mode,
      transactionId: payment.transactionId,
      paymentDate: payment.paymentDate,
      dueDate: payment.dueDate,
      status: payment.status,
      notes: payment.notes,
    });
    setPageMode("form");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const backToList = () => {
    setEditingId(null);
    setForm(emptyForm);
    setPageMode("list");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const calculatePayment = () => {
    const payableAmount = Math.max(
      Number(form.amount || 0) - Number(form.discount || 0),
      0
    );

    if (form.status === "Paid" || form.status === "Refunded") {
      return {
        paidAmount: payableAmount,
        dueAmount: 0,
      };
    }

    return {
      paidAmount: 0,
      dueAmount: payableAmount,
    };
  };

  const handleSavePayment = (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.studentName.trim() || !form.phone.trim() || !form.course.trim()) {
      alert("Please fill student name, phone and course.");
      return;
    }

    if (Number(form.amount) <= 0) {
      alert("Amount valid hona chahiye.");
      return;
    }

    const { paidAmount, dueAmount } = calculatePayment();

    if (editingId) {
      setPayments((prev) =>
        prev.map((payment) =>
          payment.id === editingId
            ? {
                ...payment,
                ...form,
                paidAmount,
                dueAmount,
              }
            : payment
        )
      );
    } else {
      const newPayment: Payment = {
        id: Date.now(),
        invoiceId: `MED-INV-${Date.now().toString().slice(-5)}`,
        ...form,
        paidAmount,
        dueAmount,
        receiptSent: false,
      };

      setPayments((prev) => [newPayment, ...prev]);
    }

    backToList();
  };

  const handleDelete = (id: number) => {
    const confirmDelete = window.confirm(
      "Are you sure? Ye payment record delete ho jayega."
    );

    if (confirmDelete) {
      setPayments((prev) => prev.filter((payment) => payment.id !== id));
    }
  };

  const markReceiptSent = (id: number) => {
    setPayments((prev) =>
      prev.map((payment) =>
        payment.id === id ? { ...payment, receiptSent: true } : payment
      )
    );
  };

  if (pageMode === "form") {
    const payableAmount = Math.max(
      Number(form.amount || 0) - Number(form.discount || 0),
      0
    );

    return (
      <div className="w-full max-w-full min-w-0 overflow-hidden space-y-5 sm:space-y-6">
        <section className="relative overflow-hidden rounded-[24px] bg-slate-950 p-5 text-white shadow-xl shadow-slate-300/40 sm:rounded-[30px] sm:p-8">
          <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-blue-500/30 blur-3xl" />
          <div className="absolute -bottom-24 left-1/2 h-72 w-72 rounded-full bg-cyan-400/20 blur-3xl" />

          <div className="relative flex min-w-0 flex-col justify-between gap-5 lg:flex-row lg:items-center">
            <div className="min-w-0">
              <div className="mb-4 inline-flex rounded-full border border-white/10 bg-white/10 px-4 py-2 text-xs font-black uppercase tracking-wide text-cyan-100">
                {editingId ? "Edit Payment" : "Add Payment"}
              </div>

              <h1 className="max-w-3xl text-2xl font-black tracking-tight sm:text-4xl">
                {editingId
                  ? "Update payment record"
                  : "Create new payment entry"}
              </h1>

              <p className="mt-3 max-w-2xl text-sm font-semibold leading-6 text-slate-300">
                Student payment, invoice, discount, transaction, due amount aur
                receipt status yaha se manage karo.
              </p>
            </div>

            <button
              type="button"
              onClick={backToList}
              className="w-fit shrink-0 rounded-2xl bg-white px-5 py-3 text-sm font-black text-slate-950 hover:bg-cyan-50"
            >
              ← Back to Payments
            </button>
          </div>
        </section>

        <form
          onSubmit={handleSavePayment}
          className="grid min-w-0 gap-6 2xl:grid-cols-[minmax(0,1fr)_340px]"
        >
          <section className="min-w-0 space-y-6">
            <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm sm:rounded-[28px] sm:p-6">
              <h2 className="text-xl font-black text-slate-950">
                Student Details
              </h2>

              <div className="mt-5 grid min-w-0 gap-4 sm:grid-cols-2">
                <FormInput
                  label="Student Name"
                  value={form.studentName}
                  placeholder="Enter student name"
                  onChange={(value) => setForm({ ...form, studentName: value })}
                />

                <FormInput
                  label="Phone Number"
                  value={form.phone}
                  placeholder="Enter phone number"
                  onChange={(value) => setForm({ ...form, phone: value })}
                />

                <div className="sm:col-span-2">
                  <FormInput
                    label="Email"
                    type="email"
                    value={form.email}
                    placeholder="Enter email"
                    onChange={(value) => setForm({ ...form, email: value })}
                  />
                </div>
              </div>
            </div>

            <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm sm:rounded-[28px] sm:p-6">
              <h2 className="text-xl font-black text-slate-950">
                Course & Batch
              </h2>

              <div className="mt-5 grid min-w-0 gap-4 sm:grid-cols-2">
                <SelectInput
                  label="Course"
                  value={form.course}
                  options={[
                    "NEET Complete Preparation",
                    "JEE Main + Advanced",
                    "Class 8 Science Foundation",
                    "Class 7 Maths Booster",
                    "Test Series",
                  ]}
                  onChange={(value) => setForm({ ...form, course: value })}
                />

                <SelectInput
                  label="Batch"
                  value={form.batch}
                  options={[
                    "NEET 2026 Morning Batch",
                    "JEE Main Target Batch",
                    "Class 8 Science Foundation",
                    "Class 7 Maths Booster",
                    "Online Test Batch",
                  ]}
                  onChange={(value) => setForm({ ...form, batch: value })}
                />
              </div>
            </div>

            <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm sm:rounded-[28px] sm:p-6">
              <h2 className="text-xl font-black text-slate-950">
                Payment Details
              </h2>

              <div className="mt-5 grid min-w-0 gap-4 sm:grid-cols-2">
                <FormInput
                  label="Course Amount"
                  type="number"
                  value={form.amount.toString()}
                  placeholder="4999"
                  onChange={(value) =>
                    setForm({ ...form, amount: Number(value) })
                  }
                />

                <FormInput
                  label="Discount"
                  type="number"
                  value={form.discount.toString()}
                  placeholder="0"
                  onChange={(value) =>
                    setForm({ ...form, discount: Number(value) })
                  }
                />

                <SelectInput
                  label="Payment Mode"
                  value={form.mode}
                  options={["UPI", "Card", "Net Banking", "Cash", "Wallet"]}
                  onChange={(value) =>
                    setForm({ ...form, mode: value as PaymentMode })
                  }
                />

                <SelectInput
                  label="Payment Status"
                  value={form.status}
                  options={["Paid", "Pending", "Failed", "Refunded"]}
                  onChange={(value) =>
                    setForm({ ...form, status: value as PaymentStatus })
                  }
                />

                <FormInput
                  label="Transaction ID"
                  value={form.transactionId}
                  placeholder="Enter transaction id"
                  onChange={(value) =>
                    setForm({ ...form, transactionId: value })
                  }
                />

                <FormInput
                  label="Payment Date"
                  type="date"
                  value={form.paymentDate}
                  placeholder=""
                  onChange={(value) =>
                    setForm({ ...form, paymentDate: value })
                  }
                />

                <FormInput
                  label="Due Date"
                  type="date"
                  value={form.dueDate}
                  placeholder=""
                  onChange={(value) => setForm({ ...form, dueDate: value })}
                />
              </div>

              <div className="mt-5">
                <label className="mb-2 block text-xs font-black uppercase tracking-wide text-slate-500">
                  Notes
                </label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Add payment note..."
                  rows={4}
                  className="w-full resize-none rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold text-slate-700 outline-none placeholder:text-slate-400 focus:border-blue-500 focus:bg-white"
                />
              </div>
            </div>
          </section>

          <aside className="min-w-0 space-y-6">
            <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm sm:rounded-[28px]">
              <h2 className="text-xl font-black text-slate-950">
                Payment Preview
              </h2>

              <div className="mt-5 rounded-2xl bg-slate-50 p-4">
                <p className="text-xs font-black uppercase tracking-wide text-slate-400">
                  {form.status} • {form.mode}
                </p>

                <h3 className="mt-3 break-words text-sm font-black leading-6 text-slate-950">
                  {form.studentName || "Student name preview..."}
                </h3>

                <p className="mt-1 break-words text-xs font-bold text-slate-500">
                  {form.course}
                </p>

                <div className="mt-4 grid grid-cols-2 gap-3">
                  <PreviewBox
                    label="Amount"
                    value={`₹${form.amount.toLocaleString()}`}
                  />
                  <PreviewBox
                    label="Discount"
                    value={`₹${form.discount.toLocaleString()}`}
                  />
                  <PreviewBox
                    label="Payable"
                    value={`₹${payableAmount.toLocaleString()}`}
                  />
                  <PreviewBox label="Mode" value={form.mode} />
                </div>
              </div>
            </div>

            <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm sm:rounded-[28px]">
              <h2 className="text-xl font-black text-slate-950">
                Receipt Settings
              </h2>

              <div className="mt-5 grid gap-3 sm:grid-cols-3 2xl:grid-cols-1">
                <InfoBox label="Auto Invoice" value="Enabled" />
                <InfoBox label="Receipt Format" value="PDF + Email" />
                <InfoBox label="GST/Tax" value="Optional" />
              </div>
            </div>

            <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-xl sm:rounded-[28px] 2xl:sticky 2xl:bottom-5">
              <div className="grid gap-3 sm:grid-cols-3 2xl:grid-cols-1">
                <button
                  type="submit"
                  className="rounded-2xl bg-blue-600 px-5 py-3 text-sm font-black text-white shadow-lg shadow-blue-600/20 hover:bg-blue-700"
                >
                  {editingId ? "Update Payment" : "Create Payment"}
                </button>

                <button
                  type="button"
                  onClick={() => setForm({ ...form, status: "Pending" })}
                  className="rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-black text-slate-700 hover:bg-slate-50"
                >
                  Mark as Pending
                </button>

                <button
                  type="button"
                  onClick={backToList}
                  className="rounded-2xl bg-slate-100 px-5 py-3 text-center text-sm font-black text-slate-700 hover:bg-slate-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          </aside>
        </form>
      </div>
    );
  }

  return (
    <div className="w-full max-w-full min-w-0 overflow-hidden space-y-5 sm:space-y-6">
      <section className="relative overflow-hidden rounded-[24px] bg-slate-950 p-5 text-white shadow-xl shadow-slate-300/40 sm:rounded-[30px] sm:p-8">
        <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-blue-500/30 blur-3xl" />
        <div className="absolute -bottom-24 left-1/2 h-72 w-72 rounded-full bg-cyan-400/20 blur-3xl" />

        <div className="relative flex min-w-0 flex-col justify-between gap-5 xl:flex-row xl:items-center">
          <div className="min-w-0">
            <div className="mb-4 inline-flex rounded-full border border-white/10 bg-white/10 px-4 py-2 text-xs font-black uppercase tracking-wide text-cyan-100">
              Payment Management
            </div>

            <h1 className="max-w-3xl text-2xl font-black tracking-tight sm:text-4xl">
              Manage fees, invoices and transactions
            </h1>

            <p className="mt-3 max-w-2xl text-sm font-semibold leading-6 text-slate-300">
              Admin yaha se student fees, pending payment, receipt, refund,
              transaction aur invoice records manage kar sakta hai.
            </p>
          </div>

          <div className="flex shrink-0 flex-wrap gap-3">
            <button className="rounded-2xl border border-white/15 bg-white/10 px-5 py-3 text-sm font-black text-white hover:bg-white/15">
              Export Report
            </button>

            <button className="rounded-2xl border border-white/15 bg-white/10 px-5 py-3 text-sm font-black text-white hover:bg-white/15">
              Download Invoices
            </button>

            <button
              onClick={openCreatePage}
              className="rounded-2xl bg-white px-5 py-3 text-sm font-black text-slate-950 hover:bg-cyan-50"
            >
              + Add Payment
            </button>
          </div>
        </div>
      </section>

      <section className="grid min-w-0 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          title="Total Revenue"
          value={`₹${totalRevenue.toLocaleString()}`}
          note="Paid payments"
        />
        <StatCard
          title="Pending Amount"
          value={`₹${pendingAmount.toLocaleString()}`}
          note="Need follow-up"
        />
        <StatCard
          title="Total Refunds"
          value={`₹${refundedAmount.toLocaleString()}`}
          note="Refunded amount"
        />
        <StatCard
          title="Transactions"
          value={payments.length.toString()}
          note="All records"
        />
      </section>

      <section className="min-w-0 rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm sm:rounded-[28px]">
        <div className="grid min-w-0 gap-5 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-[1fr_1fr_1.5fr]">
          <FilterButtons
            title="Status"
            options={statuses}
            active={activeStatus}
            onChange={setActiveStatus}
          />

          <FilterButtons
            title="Mode"
            options={modes}
            active={activeMode}
            onChange={setActiveMode}
          />

          <div className="min-w-0 md:col-span-2 xl:col-span-1">
            <p className="mb-2 text-xs font-black uppercase tracking-wide text-slate-400">
              Search
            </p>

            <div className="flex h-12 min-w-0 items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search student, invoice, phone, transaction..."
                className="min-w-0 flex-1 bg-transparent text-sm font-semibold outline-none placeholder:text-slate-400"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="grid min-w-0 gap-6 2xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-w-0 rounded-[24px] border border-slate-200 bg-white shadow-sm sm:rounded-[28px]">
          <div className="flex flex-col justify-between gap-4 border-b border-slate-100 p-5 sm:flex-row sm:items-center">
            <div>
              <h2 className="text-xl font-black text-slate-950">
                All Payments
              </h2>
              <p className="mt-1 text-sm font-semibold text-slate-500">
                {filteredPayments.length} payment records found
              </p>
            </div>

            <button
              onClick={openCreatePage}
              className="w-fit rounded-2xl bg-blue-600 px-4 py-2.5 text-xs font-black text-white hover:bg-blue-700"
            >
              + Add New
            </button>
          </div>

          <div className="hidden max-w-full overflow-x-auto 2xl:block">
            <table className="w-full min-w-[980px] text-left">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50 text-xs font-black uppercase tracking-wide text-slate-400">
                  <th className="px-5 py-4">Student</th>
                  <th className="px-5 py-4">Invoice</th>
                  <th className="px-5 py-4">Course</th>
                  <th className="px-5 py-4">Amount</th>
                  <th className="px-5 py-4">Mode</th>
                  <th className="px-5 py-4">Status</th>
                  <th className="px-5 py-4">Receipt</th>
                  <th className="px-5 py-4 text-right">Action</th>
                </tr>
              </thead>

              <tbody>
                {filteredPayments.map((payment) => (
                  <tr
                    key={payment.id}
                    className="border-b border-slate-100 last:border-0 hover:bg-slate-50"
                  >
                    <td className="px-5 py-4">
                      <div className="flex min-w-0 items-center gap-3">
                        <Avatar name={payment.studentName} />
                        <div className="min-w-0">
                          <p className="break-words text-sm font-black text-slate-950">
                            {payment.studentName}
                          </p>
                          <p className="text-xs font-bold text-slate-500">
                            {payment.phone}
                          </p>
                          <p className="break-words text-xs font-bold text-slate-400">
                            {payment.email}
                          </p>
                        </div>
                      </div>
                    </td>

                    <td className="px-5 py-4">
                      <p className="text-sm font-black text-slate-800">
                        {payment.invoiceId}
                      </p>
                      <p className="text-xs font-bold text-slate-400">
                        Txn: {payment.transactionId || "--"}
                      </p>
                    </td>

                    <td className="px-5 py-4">
                      <p className="max-w-[190px] text-sm font-black text-slate-800">
                        {payment.course}
                      </p>
                      <p className="max-w-[190px] text-xs font-bold text-slate-400">
                        {payment.batch}
                      </p>
                    </td>

                    <td className="px-5 py-4">
                      <p className="text-sm font-black text-slate-950">
                        ₹{payment.paidAmount.toLocaleString()}
                      </p>
                      <p className="text-xs font-bold text-slate-400">
                        Due: ₹{payment.dueAmount.toLocaleString()}
                      </p>
                    </td>

                    <td className="px-5 py-4">
                      <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-black text-blue-700">
                        {payment.mode}
                      </span>
                    </td>

                    <td className="px-5 py-4">
                      <StatusBadge status={payment.status} />
                    </td>

                    <td className="px-5 py-4">
                      {payment.receiptSent ? (
                        <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-black text-emerald-700">
                          Sent
                        </span>
                      ) : (
                        <button
                          onClick={() => markReceiptSent(payment.id)}
                          className="rounded-full bg-orange-50 px-3 py-1 text-xs font-black text-orange-700 hover:bg-orange-100"
                        >
                          Send
                        </button>
                      )}
                    </td>

                    <td className="px-5 py-4">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => openEditPage(payment)}
                          className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-black text-slate-600 hover:bg-slate-100"
                        >
                          Edit
                        </button>

                        <button
                          onClick={() => handleDelete(payment.id)}
                          className="rounded-xl border border-red-100 bg-red-50 px-3 py-2 text-xs font-black text-red-600 hover:bg-red-100"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid gap-4 p-4 sm:p-5 2xl:hidden">
            {filteredPayments.map((payment) => (
              <div
                key={payment.id}
                className="min-w-0 rounded-[22px] border border-slate-100 bg-slate-50 p-4"
              >
                <div className="mb-3 flex flex-wrap gap-2">
                  <StatusBadge status={payment.status} />
                  <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-black text-blue-700">
                    {payment.mode}
                  </span>
                </div>

                <div className="flex min-w-0 items-center gap-3">
                  <Avatar name={payment.studentName} />
                  <div className="min-w-0">
                    <h3 className="break-words text-sm font-black text-slate-950">
                      {payment.studentName}
                    </h3>
                    <p className="break-words text-xs font-bold text-slate-500">
                      {payment.invoiceId}
                    </p>
                  </div>
                </div>

                <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  <InfoBox label="Course" value={payment.course} />
                  <InfoBox
                    label="Paid"
                    value={`₹${payment.paidAmount.toLocaleString()}`}
                  />
                  <InfoBox
                    label="Due"
                    value={`₹${payment.dueAmount.toLocaleString()}`}
                  />
                  <InfoBox label="Date" value={payment.paymentDate || "--"} />
                </div>

                <div className="mt-4 flex flex-wrap gap-3">
                  <button
                    onClick={() => openEditPage(payment)}
                    className="rounded-2xl bg-slate-950 px-4 py-2.5 text-xs font-black text-white hover:bg-blue-600"
                  >
                    Edit
                  </button>

                  <button
                    onClick={() => handleDelete(payment.id)}
                    className="rounded-2xl bg-red-50 px-4 py-2.5 text-xs font-black text-red-600 hover:bg-red-100"
                  >
                    Delete
                  </button>

                  {!payment.receiptSent && (
                    <button
                      onClick={() => markReceiptSent(payment.id)}
                      className="rounded-2xl bg-orange-50 px-4 py-2.5 text-xs font-black text-orange-700 hover:bg-orange-100"
                    >
                      Send Receipt
                    </button>
                  )}
                </div>
              </div>
            ))}

            {filteredPayments.length === 0 && (
              <div className="rounded-2xl bg-slate-50 p-6 text-center text-sm font-bold text-slate-500">
                No payment records found.
              </div>
            )}
          </div>
        </div>

        <aside className="min-w-0 space-y-6">
          <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm sm:rounded-[28px]">
            <h2 className="text-xl font-black text-slate-950">Admin Tools</h2>

            <div className="mt-5 grid gap-3 sm:grid-cols-2 2xl:grid-cols-1">
              {[
                "Add Payment",
                "Send Payment Reminder",
                "Download Invoice",
                "Export Payment Report",
                "Create Refund Request",
                "Gateway Settlement",
              ].map((item) => (
                <button
                  key={item}
                  onClick={() => item === "Add Payment" && openCreatePage()}
                  className="group flex items-center justify-between rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3 text-left text-sm font-black text-slate-700 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
                >
                  <span className="break-words">{item}</span>
                  <span className="shrink-0 transition group-hover:translate-x-1">
                    →
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm sm:rounded-[28px]">
            <h2 className="text-xl font-black text-slate-950">
              Collection Health
            </h2>

            <div className="mt-5 space-y-4">
              <QualityItem
                label="Paid Collection"
                value="78%"
                color="bg-emerald-500"
              />
              <QualityItem
                label="Pending Follow-up"
                value="42%"
                color="bg-orange-500"
              />
              <QualityItem
                label="Failed Payments"
                value="18%"
                color="bg-red-500"
              />
            </div>
          </div>

          <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm sm:rounded-[28px]">
            <h2 className="text-xl font-black text-slate-950">
              Pending Follow-ups
            </h2>

            <div className="mt-5 space-y-4">
              {payments
                .filter(
                  (payment) =>
                    payment.status === "Pending" || payment.status === "Failed"
                )
                .slice(0, 4)
                .map((payment) => (
                  <div
                    key={payment.id}
                    className="rounded-2xl border border-slate-100 bg-slate-50 p-4"
                  >
                    <div className="flex min-w-0 items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="break-words text-sm font-black leading-5 text-slate-800">
                          {payment.studentName}
                        </p>
                        <p className="mt-1 break-words text-xs font-bold text-slate-400">
                          Due: ₹{payment.dueAmount.toLocaleString()} •{" "}
                          {payment.phone}
                        </p>
                      </div>
                      <StatusBadge status={payment.status} />
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </aside>
      </section>
    </div>
  );
};

const StatCard = ({
  title,
  value,
  note,
}: {
  title: string;
  value: string;
  note: string;
}) => {
  return (
    <div className="min-w-0 rounded-[22px] border border-slate-200 bg-white p-5 shadow-sm sm:rounded-[24px]">
      <p className="text-sm font-bold text-slate-500">{title}</p>
      <h2 className="mt-2 break-words text-3xl font-black text-slate-950">
        {value}
      </h2>
      <p className="mt-1 text-xs font-bold text-emerald-600">{note}</p>
    </div>
  );
};

const FilterButtons = ({
  title,
  options,
  active,
  onChange,
}: {
  title: string;
  options: string[];
  active: string;
  onChange: (value: string) => void;
}) => {
  return (
    <div className="min-w-0">
      <p className="mb-2 text-xs font-black uppercase tracking-wide text-slate-400">
        {title}
      </p>

      <div className="flex flex-wrap gap-2">
        {options.map((item) => (
          <button
            key={item}
            onClick={() => onChange(item)}
            className={`rounded-2xl px-3 py-2 text-xs font-black transition ${
              active === item
                ? "bg-slate-950 text-white"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            {item}
          </button>
        ))}
      </div>
    </div>
  );
};

const StatusBadge = ({ status }: { status: PaymentStatus }) => {
  return (
    <span
      className={`inline-flex shrink-0 rounded-full px-3 py-1 text-xs font-black ${
        status === "Paid"
          ? "bg-emerald-50 text-emerald-700"
          : status === "Pending"
          ? "bg-orange-50 text-orange-700"
          : status === "Failed"
          ? "bg-red-50 text-red-700"
          : "bg-blue-50 text-blue-700"
      }`}
    >
      {status}
    </span>
  );
};

const Avatar = ({ name }: { name: string }) => {
  const initials = name
    .split(" ")
    .map((word) => word[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-950 text-sm font-black text-white">
      {initials || "ST"}
    </div>
  );
};

const InfoBox = ({ label, value }: { label: string; value: string }) => {
  return (
    <div className="min-w-0 rounded-2xl bg-white p-3">
      <p className="text-xs font-bold text-slate-400">{label}</p>
      <p className="mt-1 break-words text-sm font-black text-slate-900">
        {value}
      </p>
    </div>
  );
};

const QualityItem = ({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color: string;
}) => {
  return (
    <div>
      <div className="mb-2 flex justify-between gap-3 text-sm font-black text-slate-700">
        <span>{label}</span>
        <span>{value}</span>
      </div>

      <div className="h-2 rounded-full bg-slate-100">
        <div className={`h-2 rounded-full ${color}`} style={{ width: value }} />
      </div>
    </div>
  );
};

const FormInput = ({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
}: {
  label: string;
  value: string;
  placeholder: string;
  type?: string;
  onChange: (value: string) => void;
}) => {
  return (
    <div className="min-w-0">
      <label className="mb-2 block text-xs font-black uppercase tracking-wide text-slate-500">
        {label}
      </label>

      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-bold text-slate-700 outline-none placeholder:text-slate-400 focus:border-blue-500 focus:bg-white"
      />
    </div>
  );
};

const SelectInput = ({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
}) => {
  return (
    <div className="min-w-0">
      <label className="mb-2 block text-xs font-black uppercase tracking-wide text-slate-500">
        {label}
      </label>

      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-bold text-slate-700 outline-none focus:border-blue-500 focus:bg-white"
      >
        {options.map((item) => (
          <option key={item}>{item}</option>
        ))}
      </select>
    </div>
  );
};

const PreviewBox = ({ label, value }: { label: string; value: string }) => {
  return (
    <div className="min-w-0 rounded-2xl bg-white p-3">
      <p className="text-xs font-bold text-slate-400">{label}</p>
      <p className="mt-1 break-words text-sm font-black text-slate-950">
        {value}
      </p>
    </div>
  );
};

export default AdminPayments;